import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where
} from 'firebase/firestore';
import { db } from '../../firebase';

const UserManagement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    userType: '',
    status: '',
    isAdmin: ''
  });
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (currentUser) {
      checkAdminStatus();
    } else {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    applyFilters();
  }, [users, filters]);

  const checkAdminStatus = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists() && userDoc.data().isAdmin) {
        setIsAdmin(true);
        fetchUsers();
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      navigate('/');
    }
  };

  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = [];
      
      usersSnapshot.forEach((doc) => {
        usersData.push({
          id: doc.id,
          ...doc.data()
        });
      });

      // Sort by creation date (newest first)
      usersData.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return bDate - aDate;
      });

      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(user => 
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm) ||
        user.email?.toLowerCase().includes(searchTerm) ||
        user.username?.toLowerCase().includes(searchTerm)
      );
    }

    // User type filter
    if (filters.userType) {
      filtered = filtered.filter(user => user.userType === filters.userType);
    }

    // Status filter
    if (filters.status) {
      if (filters.status === 'complete') {
        filtered = filtered.filter(user => user.isProfileComplete);
      } else if (filters.status === 'incomplete') {
        filtered = filtered.filter(user => !user.isProfileComplete);
      }
    }

    // Admin filter
    if (filters.isAdmin) {
      if (filters.isAdmin === 'admin') {
        filtered = filtered.filter(user => user.isAdmin);
      } else if (filters.isAdmin === 'regular') {
        filtered = filtered.filter(user => !user.isAdmin);
      }
    }

    setFilteredUsers(filtered);
  };

  const handleEditUser = (user) => {
    setEditingUser({ ...user });
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    try {
      const userRef = doc(db, 'users', editingUser.id);
      const updateData = {
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        email: editingUser.email,
        userType: editingUser.userType,
        isAdmin: editingUser.isAdmin || false,
        isProfileComplete: editingUser.isProfileComplete || false
      };

      await updateDoc(userRef, updateData);
      
      // Update local state
      setUsers(users.map(user => 
        user.id === editingUser.id ? { ...user, ...updateData } : user
      ));
      
      setShowEditModal(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user: ' + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsers(users.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user: ' + error.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  if (!isAdmin) {
    return <div className="error">Access denied. Admin privileges required.</div>;
  }

  return (
    <div className="user-management-container">
      <div className="admin-header">
        <div className="admin-breadcrumb">
          <button onClick={() => navigate('/admin/dashboard')} className="breadcrumb-link">
            ← Back to Admin Dashboard
          </button>
        </div>
        <h2>User Management</h2>
        <p>Manage user accounts and permissions</p>
      </div>

      <div className="user-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search users..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="user-search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filters.userType}
            onChange={(e) => setFilters({ ...filters, userType: e.target.value })}
            className="user-filter-select"
          >
            <option value="">All User Types</option>
            <option value="learner">Learner</option>
            <option value="provider">Provider</option>
            <option value="both">Both</option>
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="user-filter-select"
          >
            <option value="">All Statuses</option>
            <option value="complete">Profile Complete</option>
            <option value="incomplete">Profile Incomplete</option>
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.isAdmin}
            onChange={(e) => setFilters({ ...filters, isAdmin: e.target.value })}
            className="user-filter-select"
          >
            <option value="">All Permissions</option>
            <option value="admin">Admin Users</option>
            <option value="regular">Regular Users</option>
          </select>
        </div>
      </div>

      <div className="user-content">
        <div className="user-stats">
          <div className="user-stat-item">
            <span className="user-stat-label">Total Users:</span>
            <span className="user-stat-value">{users.length}</span>
          </div>
          <div className="user-stat-item">
            <span className="user-stat-label">Filtered:</span>
            <span className="user-stat-value">{filteredUsers.length}</span>
          </div>
        </div>

        <div className="users-grid">
          {filteredUsers.map(user => (
            <div key={user.id} className="user-card">
              <div className="user-card-header">
                <div className="user-avatar-section">
                  {user.profilePicture ? (
                    <img src={user.profilePicture} alt={user.firstName} className="user-avatar-image" />
                  ) : (
                    <div className="user-avatar-default">
                      {user.firstName?.[0]?.toUpperCase()}{user.lastName?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="user-name-section">
                    <div className="user-full-name">{user.firstName} {user.lastName}</div>
                    {user.username && <div className="user-username">@{user.username}</div>}
                  </div>
                </div>
                <div className="user-badges">
                  <span className={`user-admin-badge ${user.isAdmin ? 'admin' : 'regular'}`}>
                    {user.isAdmin ? 'Admin' : 'User'}
                  </span>
                </div>
              </div>
              
              <div className="user-card-content">
                <div className="user-email-section">
                  <div className="field-label">Email</div>
                  <div className="field-value">{user.email}</div>
                </div>
                
                <div className="user-details-grid">
                  <div className="detail-item">
                    <div className="detail-label">Type</div>
                    <span className={`user-type-badge ${user.userType}`}>
                      {user.userType}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-label">Status</div>
                    <span className={`user-status-badge ${user.isProfileComplete ? 'complete' : 'incomplete'}`}>
                      {user.isProfileComplete ? 'Complete' : 'Incomplete'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="user-card-footer">
                <div className="user-created-section">
                  <div className="created-label">Created</div>
                  <div className="created-date">{formatDate(user.createdAt)}</div>
                </div>
                <div className="user-actions">
                  <button 
                    className="user-edit-btn"
                    onClick={() => handleEditUser(user)}
                  >
                    Edit
                  </button>
                  <button 
                    className="user-delete-btn"
                    onClick={() => handleDeleteUser(user.id)}
                    disabled={user.id === currentUser.uid}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="modal-overlay">
          <div className="modal-content edit-user-modal">
            <div className="modal-header">
              <h3>Edit User</h3>
              <button 
                className="modal-close-btn"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={editingUser.firstName || ''}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    firstName: e.target.value
                  })}
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={editingUser.lastName || ''}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    lastName: e.target.value
                  })}
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    email: e.target.value
                  })}
                />
              </div>

              <div className="form-group">
                <label>User Type</label>
                <select
                  value={editingUser.userType || 'learner'}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    userType: e.target.value
                  })}
                >
                  <option value="learner">Learner</option>
                  <option value="provider">Provider</option>
                  <option value="both">Both</option>
                </select>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editingUser.isAdmin || false}
                    onChange={(e) => setEditingUser({
                      ...editingUser,
                      isAdmin: e.target.checked
                    })}
                  />
                  Admin User
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editingUser.isProfileComplete || false}
                    onChange={(e) => setEditingUser({
                      ...editingUser,
                      isProfileComplete: e.target.checked
                    })}
                  />
                  Profile Complete
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={handleSaveUser}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;